import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { DialogSelect } from "@/components/ui/dialog-select";
import { Customer } from "@/types/customer";
import { Pencil } from "lucide-react";

interface EditCustomerDialogProps {
  customer: Customer;
  onUpdate: (customer: Customer) => void;
  jobs: string[];
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function EditCustomerDialog({ customer, onUpdate, jobs, open: controlledOpen, onOpenChange }: EditCustomerDialogProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;
  const setOpen = (value: boolean) => {
    if (isControlled) {
      onOpenChange?.(value);
    } else {
      setInternalOpen(value);
    }
  };
  
  const [formData, setFormData] = useState({
    fullName: customer.fullName,
    phone1: customer.phone1,
    phone2: customer.phone2,
    guarantorName: customer.guarantorName,
    guarantorPhone: customer.guarantorPhone,
    job: customer.job,
    address: customer.address,
    notes: customer.notes,
  });

  const jobOptions = jobs.map(job => ({ value: job, label: job }));

  useEffect(() => {
    setFormData({
      fullName: customer.fullName,
      phone1: customer.phone1,
      phone2: customer.phone2,
      guarantorName: customer.guarantorName,
      guarantorPhone: customer.guarantorPhone,
      job: customer.job,
      address: customer.address,
      notes: customer.notes,
    });
  }, [customer]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onUpdate({
      ...customer,
      fullName: formData.fullName,
      phone1: formData.phone1,
      phone2: formData.phone2,
      guarantorName: formData.guarantorName,
      guarantorPhone: formData.guarantorPhone,
      job: formData.job,
      address: formData.address,
      notes: formData.notes,
    });
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {!isControlled && (
        <DialogTrigger asChild>
          <Button variant="ghost" size="icon" className="h-8 w-8 bg-blue-500/10 text-blue-500 hover:text-blue-500 hover:bg-blue-500/20" title="دەستکاری">
            <Pencil className="h-4 w-4" />
          </Button>
        </DialogTrigger>
      )}
      <DialogContent className="w-[95vw] max-w-2xl max-h-[90vh] overflow-y-auto" dir="rtl">
        <DialogHeader className="text-right">
          <DialogTitle className="text-right">دەستکاری کڕیار</DialogTitle>
          <DialogDescription className="text-right">زانیاریەکانی کڕیار دەستکاری بکە</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* کۆد و ناو */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="customerCode">کۆدی کڕیار</Label>
              <Input
                id="customerCode"
                type="number"
                value={customer.customerCode}
                disabled
                className="bg-muted text-center h-10"
                dir="ltr"
              />
            </div>
            <div className="space-y-2 md:col-span-3">
              <Label htmlFor="fullName">ناوی تەواو *</Label>
              <Input
                id="fullName"
                value={formData.fullName}
                onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                required
                placeholder="ناوی تەواو"
                className="h-10"
              />
            </div>
          </div>

          {/* پیشە */}
          <div className="space-y-2">
            <Label>پیشە</Label>
            <DialogSelect
              value={formData.job}
              onChange={(value) => setFormData({ ...formData, job: value })}
              options={jobOptions}
              placeholder="هەڵبژێرە"
              searchable={true}
              searchPlaceholder="گەڕان..."
              emptyMessage={jobs.length === 0 ? "هیچ پیشەیەک لە داتابەیس نییە" : "هیچ پیشەیەک نەدۆزرایەوە"}
              allowClear={true}
              clearLabel="هیچی"
            />
          </div>

          {/* ژمارە مۆبایلەکان */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="phone1">ژمارە مۆبایل ١ *</Label>
              <Input
                id="phone1"
                value={formData.phone1}
                onChange={(e) => setFormData({ ...formData, phone1: e.target.value })}
                required
                placeholder="07xxxxxxxxx"
                dir="ltr"
                className="text-center"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone2">ژمارە مۆبایل ٢</Label>
              <Input
                id="phone2"
                value={formData.phone2}
                onChange={(e) => setFormData({ ...formData, phone2: e.target.value })}
                placeholder="07xxxxxxxxx"
                dir="ltr"
                className="text-center"
              />
            </div>
          </div>

          {/* زانیاریەکانی کەفیل */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="guarantorName">ناوی کەفیل</Label>
              <Input
                id="guarantorName"
                value={formData.guarantorName}
                onChange={(e) => setFormData({ ...formData, guarantorName: e.target.value })}
                placeholder="ناوی کەفیل"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="guarantorPhone">ژمارەی کەفیل</Label>
              <Input
                id="guarantorPhone"
                value={formData.guarantorPhone}
                onChange={(e) => setFormData({ ...formData, guarantorPhone: e.target.value })}
                placeholder="07xxxxxxxxx"
                dir="ltr"
                className="text-center"
              />
            </div>
          </div>

          {/* ناونیشان */}
          <div className="space-y-2">
            <Label htmlFor="address">ناونیشان</Label>
            <Input
              id="address"
              value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              placeholder="ناونیشان"
            />
          </div>

          {/* تێبینی */}
          <div className="space-y-2">
            <Label htmlFor="notes">تێبینی</Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="تێبینی..."
              rows={2}
              className="resize-none"
            />
          </div>

          {/* دوگمەکان */}
          <div className="flex flex-col-reverse sm:flex-row gap-2 justify-end pt-4">
            <Button type="button" variant="outline" onClick={() => setOpen(false)} className="w-full sm:w-auto">
              پاشگەزبوونەوە
            </Button>
            <Button type="submit" className="w-full sm:w-auto">
              پاشەکەوتکردن
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
